import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom'
import { Container, Row, Col, Card, Button, Spinner, Alert, Badge, Table, Modal, Form, Nav } from 'react-bootstrap'
import { productionAPI } from '../api/productionAPI'
import { recipeAPI } from '../api/recipeAPI'
import { locationAPI } from '../api/locationAPI'
import { warehouseAPI } from '../api/warehouseAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import { formatDate, formatDateTime, formatUnitCost } from '../utils/commonUtils'
import { reasonOptions } from '../utils/disposeUtils'
import { getWasteBadgeVariant } from '../utils/wasteUtils'
import PickInventoryModal from '../components/PickInventoryModal'
import ConfirmationModal from '../components/ConfirmationModal'
import CopyableText from '../components/CopyableText'
import QuantityPicker from '../components/QuantityPicker'
import QuantityDisplay from '../components/QuantityDisplay'
import Select from 'react-select'
import '../styles/selectControl.css'

function ProductionDetailScreen() {
    const { id } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const [returnFilters] = useState(location.state?.returnFilters || '')

    const [productionLot, setProductionLot] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [successMessage, setSuccessMessage] = useState(null)

    // Add inventory modal state
    const [showAddInventoryModal, setShowAddInventoryModal] = useState(false)
    const [selectedIngredientForAdd, setSelectedIngredientForAdd] = useState(null)
    const [addingInventory, setAddingInventory] = useState(false)

    // Return inventory modal state
    const [showReturnModal, setShowReturnModal] = useState(false)
    const [selectedAllocationForReturn, setSelectedAllocationForReturn] = useState(null)
    const [returnQuantity, setReturnQuantity] = useState('')
    const [returnLocationId, setReturnLocationId] = useState('')
    const [returningInventory, setReturningInventory] = useState(false)
    const [warehouses, setWarehouses] = useState([])
    const [locations, setLocations] = useState([])
    const [selectedWarehouse, setSelectedWarehouse] = useState('')

    // Dispose inventory modal state
    const [showDisposeModal, setShowDisposeModal] = useState(false)
    const [selectedAllocationForDispose, setSelectedAllocationForDispose] = useState(null)
    const [disposeQuantity, setDisposeQuantity] = useState('')
    const [disposeReason, setDisposeReason] = useState('')
    const [disposeComments, setDisposeComments] = useState('')
    const [disposingInventory, setDisposingInventory] = useState(false)

    // Correct ingredient modal state
    const [showCorrectModal, setShowCorrectModal] = useState(false)
    const [selectedAllocationForCorrect, setSelectedAllocationForCorrect] = useState(null)
    const [correctNewQuantityUsed, setCorrectNewQuantityUsed] = useState('')
    const [correctingIngredient, setCorrectingIngredient] = useState(false)
    const [correctModalTab, setCorrectModalTab] = useState('reduce')
    const [correctModalLotIngredient, setCorrectModalLotIngredient] = useState(null)

    // Add missing item correction state (opened from within Correct modal)
    const [showAddItemCorrectionModal, setShowAddItemCorrectionModal] = useState(false)
    const [addingItemCorrection, setAddingItemCorrection] = useState(false)

    // Correct output quantity modal state
    const [showCorrectOutputModal, setShowCorrectOutputModal] = useState(false)
    const [correctOutputNewQuantity, setCorrectOutputNewQuantity] = useState('')
    const [correctingOutput, setCorrectingOutput] = useState(false)

    // Cancel modal state
    const [showCancelModal, setShowCancelModal] = useState(false)
    const [cancelling, setCancelling] = useState(false)


    useEffect(() => {
        fetchProductionLot()
        fetchWarehouses()
    }, [id])

    useEffect(() => {
        if (selectedWarehouse) {
            fetchLocations(selectedWarehouse)
        } else {
            setLocations([])
        }
    }, [selectedWarehouse])

    const fetchProductionLot = async () => {
        try {
            setLoading(true)
            const data = await productionAPI.getById(id)
            setProductionLot(data)
        } catch (err) {
            setError(formatErrorResponse(err) || 'Failed to load production LOT')
        } finally {
            setLoading(false)
        }
    }

    const fetchWarehouses = async () => {
        try {
            const data = await warehouseAPI.getAll()
            const activeWarehouses = Array.isArray(data)
                ? data.filter(w => w.is_active)
                : (data.results || []).filter(w => w.is_active)
            setWarehouses(activeWarehouses)
        } catch (error) {
            console.error('Error fetching warehouses:', error)
        }
    }

    const fetchLocations = async (warehouseId) => {
        try {
            const data = await locationAPI.getAll(warehouseId, undefined, undefined, undefined, undefined, true)
            const activeLocations = Array.isArray(data)
                ? data.filter(l => l.is_active)
                : (data.results || []).filter(l => l.is_active)
            setLocations(activeLocations)
        } catch (error) {
            console.error('Error fetching locations:', error)
            setLocations([])
        }
    }

    const getStatusBadge = (status) => {
        switch (status) {
            case 'in_progress':
                return <Badge bg="primary">In Progress</Badge>
            case 'completed':
                return <Badge bg="success">Completed</Badge>
            case 'cancelled':
                return <Badge bg="secondary">Cancelled</Badge>
            default:
                return <Badge bg="light" text="dark">{status}</Badge>
        }
    }

    // Handle opening add inventory modal
    const openAddInventoryModal = (lotIngredient) => {
        setSelectedIngredientForAdd(lotIngredient)
        setShowAddInventoryModal(true)
    }

    // Handle add inventory confirmation
    const handleAddInventoryConfirm = async (allocations) => {
        if (!selectedIngredientForAdd) return

        setAddingInventory(true)
        setError(null)

        try {
            // Convert allocations to the format expected by the API
            const apiAllocations = Object.entries(allocations).map(([inventoryItemId, quantity]) => ({
                ingredient_id: selectedIngredientForAdd.recipe_ingredient,
                inventory_item_id: parseInt(inventoryItemId),
                quantity: parseFloat(quantity)
            })).filter(a => a.quantity > 0)

            if (apiAllocations.length === 0) {
                setError('No valid allocations to add')
                return
            }

            await productionAPI.addInventory(id, apiAllocations)
            setSuccessMessage('Inventory added successfully')
            await fetchProductionLot()
        } catch (err) {
            setError(formatErrorResponse(err) || 'Failed to add inventory')
        } finally {
            setAddingInventory(false)
            setShowAddInventoryModal(false)
            setSelectedIngredientForAdd(null)
        }
    }

    // Handle opening return modal
    const openReturnModal = (allocation) => {
        const available = parseFloat(allocation.quantity_allocated) - parseFloat(allocation.quantity_returned || 0) - parseFloat(allocation.quantity_disposed || 0)
        setSelectedAllocationForReturn({ ...allocation, _available: available })
        setReturnQuantity(available.toString())
        setReturnLocationId('')
        setSelectedWarehouse('')
        setShowReturnModal(true)
    }

    // Handle return inventory
    const handleReturnInventory = async () => {
        if (!selectedAllocationForReturn || !returnLocationId || !returnQuantity) {
            setError('Please fill in all return fields')
            return
        }

        const qty = parseFloat(returnQuantity)
        if (isNaN(qty) || qty <= 0 || qty > selectedAllocationForReturn._available) {
            setError('Invalid return quantity')
            return
        }

        setReturningInventory(true)
        setError(null)

        try {
            await productionAPI.returnInventory(id, [{
                production_lot_inventory_item_id: selectedAllocationForReturn._id,
                quantity: qty,
                return_location_id: parseInt(returnLocationId)
            }])
            setSuccessMessage('Inventory returned successfully')
            await fetchProductionLot()
        } catch (err) {
            setError(formatErrorResponse(err) || 'Failed to return inventory')
        } finally {
            setReturningInventory(false)
            setShowReturnModal(false)
            setSelectedAllocationForReturn(null)
        }
    }

    // Handle cancel production
    const handleCancelProduction = async () => {
        setCancelling(true)
        setError(null)

        try {
            await productionAPI.cancel(id)
            setSuccessMessage('Production LOT cancelled')
            await fetchProductionLot()
        } catch (err) {
            setError(formatErrorResponse(err) || 'Failed to cancel production LOT')
        } finally {
            setCancelling(false)
            setShowCancelModal(false)
        }
    }

    // Handle opening dispose modal
    const openDisposeModal = (allocation) => {
        const available = parseFloat(allocation.quantity_allocated) - parseFloat(allocation.quantity_returned || 0) - parseFloat(allocation.quantity_disposed || 0)
        setSelectedAllocationForDispose({ ...allocation, _available: available })
        setDisposeQuantity('')
        setDisposeReason('')
        setDisposeComments('')
        setShowDisposeModal(true)
    }

    // Handle dispose inventory
    const handleDisposeInventory = async () => {
        if (!selectedAllocationForDispose || !disposeQuantity || !disposeReason) {
            setError('Please fill in quantity and reason')
            return
        }

        const qty = parseFloat(disposeQuantity)
        if (isNaN(qty) || qty <= 0 || qty > selectedAllocationForDispose._available) {
            setError('Invalid dispose quantity')
            return
        }

        setDisposingInventory(true)
        setError(null)

        try {
            await productionAPI.disposeInventory(id, [{
                production_lot_inventory_item_id: selectedAllocationForDispose._id,
                quantity: qty,
                reason: disposeReason,
                comments: disposeComments
            }])
            setSuccessMessage('Inventory disposed successfully')
            await fetchProductionLot()
        } catch (err) {
            setError(formatErrorResponse(err) || 'Failed to dispose inventory')
        } finally {
            setDisposingInventory(false)
            setShowDisposeModal(false)
            setSelectedAllocationForDispose(null)
        }
    }

    // Handle opening correct ingredient modal
    const openCorrectModal = (allocation, lotIngredient) => {
        setSelectedAllocationForCorrect(allocation)
        setCorrectModalLotIngredient(lotIngredient)
        setCorrectNewQuantityUsed(allocation.quantity_used?.toString() || '0')
        setCorrectModalTab('reduce')
        setSelectedWarehouse('')
        setReturnLocationId('')
        setShowCorrectModal(true)
    }

    // Handle correct ingredient
    const handleCorrectIngredient = async () => {
        if (!selectedAllocationForCorrect || !returnLocationId || !correctNewQuantityUsed) {
            setError('Please select a destination warehouse and location')
            return
        }

        const newQty = parseFloat(correctNewQuantityUsed)
        const oldQty = parseFloat(selectedAllocationForCorrect.quantity_used || 0)
        if (isNaN(newQty) || newQty < 0 || newQty >= oldQty) {
            setError('New quantity used must be less than the current quantity used')
            return
        }

        setCorrectingIngredient(true)
        setError(null)

        try {
            await productionAPI.correctIngredient(id, [{
                production_lot_inventory_item_id: selectedAllocationForCorrect._id,
                new_quantity_used: newQty,
            }], parseInt(returnLocationId))
            setSuccessMessage('Ingredient quantity corrected successfully')
            await fetchProductionLot()
        } catch (err) {
            setError(formatErrorResponse(err) || 'Failed to correct ingredient quantity')
        } finally {
            setCorrectingIngredient(false)
            setShowCorrectModal(false)
            setSelectedAllocationForCorrect(null)
        }
    }

    // Handle switching to Add Item tab — close Correct modal and open PickInventoryModal
    const handleSwitchToAddItem = () => {
        setShowCorrectModal(false)
        setShowAddItemCorrectionModal(true)
    }

    // Handle add item correction confirmation
    const handleAddItemCorrectionConfirm = async (allocations) => {
        if (!correctModalLotIngredient) return

        setAddingItemCorrection(true)
        setError(null)

        try {
            const apiAdditions = Object.entries(allocations).map(([inventoryItemId, quantity]) => ({
                recipe_ingredient_id: correctModalLotIngredient.recipe_ingredient,
                inventory_item_id: parseInt(inventoryItemId),
                quantity: parseFloat(quantity)
            })).filter(a => a.quantity > 0)

            if (apiAdditions.length === 0) {
                setError('No valid items to add')
                return
            }

            await productionAPI.addMissingItem(id, apiAdditions)
            setSuccessMessage('Missing items added successfully')
            await fetchProductionLot()
        } catch (err) {
            setError(formatErrorResponse(err) || 'Failed to add missing items')
        } finally {
            setAddingItemCorrection(false)
            setShowAddItemCorrectionModal(false)
            setCorrectModalLotIngredient(null)
        }
    }

    // Handle opening correct output quantity modal
    const openCorrectOutputModal = () => {
        setCorrectOutputNewQuantity(productionLot.actual_quantity_produced?.toString() || '')
        setShowCorrectOutputModal(true)
    }

    // Handle correct output quantity
    const handleCorrectOutputQuantity = async () => {
        const newQty = parseFloat(correctOutputNewQuantity)
        const oldQty = parseFloat(productionLot.actual_quantity_produced || 0)

        if (isNaN(newQty) || newQty <= 0) {
            setError('New output quantity must be a positive number')
            return
        }
        if (newQty === oldQty) {
            setError('New output quantity is the same as the current quantity')
            return
        }

        setCorrectingOutput(true)
        setError(null)

        try {
            await productionAPI.correctOutputQuantity(id, newQty)
            setSuccessMessage('Output quantity corrected successfully')
            await fetchProductionLot()
        } catch (err) {
            setError(formatErrorResponse(err) || 'Failed to correct output quantity')
        } finally {
            setCorrectingOutput(false)
            setShowCorrectOutputModal(false)
        }
    }

    if (loading) {
        return (
            <Container className="py-4">
                <div className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            </Container>
        )
    }

    if (!productionLot) {
        return (
            <Container className="py-4">
                <Alert variant="danger">{error || 'Production lot not found'}</Alert>
                <Button variant="outline-primary" onClick={() => navigate(`/productions${returnFilters}`)}>
                    Back to List
                </Button>
            </Container>
        )
    }

    const isInProgress = productionLot.status === 'in_progress'
    const canCorrect = productionLot.status === 'completed'
        && parseFloat(productionLot.quantity_disassembled || 0) === 0

    return (
        <Container className="py-4">
            <Row className="mb-4">
                <Col>
                    <h1>Production LOT: {productionLot.lot_number}</h1>
                </Col>
                <Col xs="auto">
                    {getStatusBadge(productionLot.status)}
                </Col>
            </Row>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {successMessage && (
                <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
                    {successMessage}
                </Alert>
            )}

            {/* Production LOT Details */}
            <Card className="mb-4">
                <Card.Header>
                    <h5 className="mb-0">Details</h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col xs="auto">
                            {productionLot.output_stock_item_image ? (
                                <img
                                    src={productionLot.output_stock_item_image}
                                    alt={productionLot.output_stock_item_name}
                                    style={{
                                        width: '140px',
                                        height: '140px',
                                        objectFit: 'contain'
                                    }}
                                />
                            ) : (
                                <div
                                    style={{
                                        width: '140px',
                                        height: '140px',
                                        backgroundColor: '#f0f0f0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    No Image
                                </div>
                            )}
                        </Col>
                        <Col md={5}>
                            <p><strong>Recipe:</strong>{' '}
                                <Link to={`/recipe/${productionLot.recipe}`} state={{ backTo: `/production/${productionLot._id}`, backLabel: 'Back to Production LOT' }}>
                                    {productionLot.recipe_name}
                                </Link>
                            </p>
                            {productionLot.recipe_item_number && (
                                <p><strong>Item #:</strong> {productionLot.recipe_item_number}</p>
                            )}
                            <p><strong>Output Product:</strong> {productionLot.output_stock_item_name}</p>
                            {productionLot.output_stock_item_brand_name && (
                                <p><strong>Brand:</strong> {productionLot.output_stock_item_brand_name}</p>
                            )}
                            <p><strong>GTIN:</strong> <CopyableText value={productionLot.output_stock_item_gtin} toastLabel="GTIN" /></p>
                            <p><strong>Target Quantity:</strong> {productionLot.target_quantity}</p>
                            <p><strong>Production Location:</strong> {productionLot.production_location_name}</p>
                            {productionLot.output_bb_date && (
                                <p><strong>Best Before Date:</strong> {formatDate(productionLot.output_bb_date)}</p>
                            )}
                        </Col>
                        <Col md={6}>
                            <p><strong>Started At:</strong> {formatDateTime(productionLot.started_at)}</p>
                            <p><strong>Started By:</strong> {productionLot.started_by_name}</p>
                            {productionLot.completed_at && (
                                <>
                                    <p><strong>Completed At:</strong> {formatDateTime(productionLot.completed_at)}</p>
                                    <p><strong>Completed By:</strong> {productionLot.completed_by_name}</p>
                                </>
                            )}
                            {productionLot.actual_quantity_produced && (
                                <p>
                                    <strong>Actual Quantity Produced:</strong> {productionLot.actual_quantity_produced}
                                    {canCorrect && (
                                        <Button
                                            variant="outline-warning"
                                            size="sm"
                                            className="ms-2"
                                            onClick={openCorrectOutputModal}
                                        >
                                            Correct
                                        </Button>
                                    )}
                                </p>
                            )}
                            {parseFloat(productionLot.quantity_disassembled || 0) > 0 && (
                                <>
                                    <p><strong>Quantity Disassembled:</strong> {productionLot.quantity_disassembled}</p>
                                    {productionLot.disassembled_at && (
                                        <p><strong>Last Disassembled At:</strong> {formatDateTime(productionLot.disassembled_at)}</p>
                                    )}
                                    {productionLot.disassembled_by_name && (
                                        <p><strong>Disassembled By:</strong> {productionLot.disassembled_by_name}</p>
                                    )}
                                </>
                            )}
                            {productionLot.total_cost && (
                                <p><strong>Total Cost:</strong> ${parseFloat(productionLot.total_cost).toFixed(2)}</p>
                            )}
                            {productionLot.unit_cost && (
                                <p><strong>Unit Cost:</strong> {formatUnitCost(productionLot.unit_cost)}</p>
                            )}
                        </Col>
                    </Row>
                    {productionLot.notes && (
                        <Row>
                            <Col>
                                <p><strong>Notes:</strong> {productionLot.notes}</p>
                            </Col>
                        </Row>
                    )}
                </Card.Body>
            </Card>

            {/* Ingredients */}
            <Card className="mb-4">
                <Card.Header>
                    <h5 className="mb-0">Ingredients</h5>
                </Card.Header>
                <Card.Body>
                    {productionLot.lot_ingredients && productionLot.lot_ingredients.length > 0 ? (
                        productionLot.lot_ingredients.map((lotIngredient) => {
                            const ri = lotIngredient.recipe_ingredient_data
                            const isWeight = ri?.input_mode === 'weight'
                            const casePack = ri?.case_pack || 1
                            const uom = ri?.unit_of_measure || ''
                            return (
                            <Card key={lotIngredient._id} className="mb-3">
                                <Card.Header>
                                    <div className="d-flex align-items-center">
                                        <span style={{ width: '160px', flexShrink: 0 }}>
                                            {ri?.item_family
                                                ? ''
                                                : <CopyableText value={ri?.gtin} toastLabel="GTIN" />}
                                        </span>
                                        <strong>{lotIngredient.ingredient_name}</strong>
                                        {isInProgress && (
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="ms-auto"
                                                onClick={() => openAddInventoryModal(lotIngredient)}
                                            >
                                                Add More
                                            </Button>
                                        )}
                                    </div>
                                    <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
                                        {(() => {
                                            const wastePctVal = parseFloat(ri?.waste_percentage) || 0
                                            const requiredQty = parseFloat(lotIngredient.required_quantity)
                                            const recipeQty = wastePctVal > 0 ? requiredQty / (1 + wastePctVal / 100) : requiredQty
                                            return (
                                                <>
                                                    {wastePctVal > 0 && (
                                                        <Badge bg="secondary">
                                                            Recipe Qty: {isWeight ? (
                                                                <span>{parseFloat(recipeQty.toFixed(3))} {ri?.weight_unit || 'lbs'}
                                                                    {ri?.weight_per_case
                                                                        ? <span> (<QuantityDisplay casePack={casePack} uom={uom} value={recipeQty / ri.weight_per_case} />)</span>
                                                                        : null}
                                                                </span>
                                                            ) : (
                                                                <QuantityDisplay casePack={casePack} uom={uom} value={recipeQty} />
                                                            )}
                                                        </Badge>
                                                    )}
                                                    <Badge bg="info">
                                                        {wastePctVal > 0 ? 'Required (incl. waste): ' : 'Required: '}{isWeight ? (
                                                            <span>{requiredQty} {ri?.weight_unit || 'lbs'}
                                                                {ri?.weight_per_case
                                                                    ? <span> (<QuantityDisplay casePack={casePack} uom={uom} value={requiredQty / ri.weight_per_case} />)</span>
                                                                    : null}
                                                            </span>
                                                        ) : (
                                                            <QuantityDisplay casePack={casePack} uom={uom} value={lotIngredient.required_quantity} />
                                                        )}
                                                    </Badge>
                                                </>
                                            )
                                        })()}
                                        <Badge bg={
                                            isWeight
                                                ? 'secondary'
                                                : parseFloat(lotIngredient.picked_quantity) >= parseFloat(lotIngredient.required_quantity)
                                                    ? 'success' : 'warning'
                                        }>
                                            Picked: {isWeight && ri?.item_family
                                                ? <span>{parseFloat(lotIngredient.picked_quantity)} units</span>
                                                : <QuantityDisplay casePack={casePack} uom={uom} value={lotIngredient.picked_quantity} />}
                                        </Badge>
                                        {lotIngredient.used_quantity !== null && (
                                            <Badge bg="secondary">
                                                Used: {isWeight && ri?.item_family
                                                    ? <span>{parseFloat(lotIngredient.used_quantity)} units</span>
                                                    : <QuantityDisplay casePack={casePack} uom={uom} value={lotIngredient.used_quantity} />}
                                            </Badge>
                                        )}
                                        {productionLot.status === 'completed' && (() => {
                                            const wastePct = lotIngredient.actual_waste_percentage != null
                                                ? parseFloat(lotIngredient.actual_waste_percentage)
                                                : null
                                            return (
                                                <Badge bg={wastePct != null ? getWasteBadgeVariant(wastePct) : 'secondary'} className="rounded-pill">
                                                    Waste: {wastePct != null ? `${wastePct.toFixed(2)}%` : '--'}
                                                </Badge>
                                            )
                                        })()}
                                    </div>
                                </Card.Header>
                                <Card.Body>
                                    {lotIngredient.inventory_allocations && lotIngredient.inventory_allocations.length > 0 ? (
                                        <Table size="sm" striped style={{ tableLayout: 'fixed' }}>
                                            {(() => {
                                                const hasUsed = lotIngredient.used_quantity !== null
                                                const hasDisposed = isInProgress || lotIngredient.inventory_allocations.some(a => parseFloat(a.quantity_disposed || 0) > 0)
                                                const hasReturned = isInProgress || lotIngredient.inventory_allocations.some(a => parseFloat(a.quantity_returned || 0) > 0)
                                                const hasDisassembled = lotIngredient.inventory_allocations.some(a => parseFloat(a.quantity_disassembled || 0) > 0)
                                                return (
                                                    <>
                                                    <colgroup>
                                                        <col style={{ width: '17%' }} />
                                                        <col style={{ width: '13%' }} />
                                                        <col style={{ width: '12%' }} />
                                                        <col style={{ width: '13%' }} />
                                                        <col style={{ width: '7%' }} />
                                                        <col style={{ width: '8%' }} />
                                                        {hasUsed && <col style={{ width: '7%' }} />}
                                                        {(hasUsed || hasReturned) && <col style={{ width: '9%' }} />}
                                                        {hasDisposed && <col style={{ width: '9%' }} />}
                                                        {hasDisassembled && <col style={{ width: '9%' }} />}
                                                        {isInProgress && <col style={{ width: '17%' }} />}
                                                        {canCorrect && <col style={{ width: '8%' }} />}
                                                    </colgroup>
                                                    <thead>
                                                        <tr>
                                                            <th>GTIN</th>
                                                            <th>Inventory Item</th>
                                                            <th>LOT</th>
                                                            <th>Original Location</th>
                                                            <th>Cost</th>
                                                            <th>Allocated</th>
                                                            {hasUsed && <th>Used</th>}
                                                            {(hasUsed || hasReturned) && <th>Returned</th>}
                                                            {hasDisposed && <th>Disposed</th>}
                                                            {hasDisassembled && <th>Restored</th>}
                                                            {isInProgress && <th className="text-end">Actions</th>}
                                                            {canCorrect && <th className="text-end">Actions</th>}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {lotIngredient.inventory_allocations.map((allocation) => {
                                                            const available = parseFloat(allocation.quantity_allocated) - parseFloat(allocation.quantity_returned || 0) - parseFloat(allocation.quantity_disposed || 0)
                                                            const aCp = allocation.stock_item_case_pack || 1
                                                            const aUom = allocation.stock_item_uom || ''
                                                            return (
                                                                <tr key={allocation._id}>
                                                                    <td><CopyableText value={allocation.stock_item_gtin} toastLabel="GTIN" /></td>
                                                                    <td>{allocation.stock_item_name || '-'}</td>
                                                                    <td>{allocation.original_lot || '-'}</td>
                                                                    <td>{allocation.original_location_name || '-'}</td>
                                                                    <td>{allocation.original_cost != null ? `$${parseFloat(allocation.original_cost).toFixed(4)}` : ''}</td>
                                                                    <td><QuantityDisplay casePack={aCp} uom={aUom} value={allocation.quantity_allocated} /></td>
                                                                    {hasUsed && <td><QuantityDisplay casePack={aCp} uom={aUom} value={allocation.quantity_used || 0} /></td>}
                                                                    {(hasUsed || hasReturned) && <td><QuantityDisplay casePack={aCp} uom={aUom} value={allocation.quantity_returned || 0} /></td>}
                                                                    {hasDisposed && <td><QuantityDisplay casePack={aCp} uom={aUom} value={allocation.quantity_disposed || 0} /></td>}
                                                                    {hasDisassembled && <td><QuantityDisplay casePack={aCp} uom={aUom} value={allocation.quantity_disassembled || 0} /></td>}
                                                                    {isInProgress && (
                                                                        <td>
                                                                            <div className="d-flex gap-1 justify-content-end">
                                                                                <Button
                                                                                    variant="outline-warning"
                                                                                    size="sm"
                                                                                    onClick={() => openReturnModal(allocation)}
                                                                                    disabled={available <= 0}
                                                                                >
                                                                                    Return
                                                                                </Button>
                                                                                <Button
                                                                                    variant="outline-danger"
                                                                                    size="sm"
                                                                                    onClick={() => openDisposeModal(allocation)}
                                                                                    disabled={available <= 0}
                                                                                >
                                                                                    Dispose
                                                                                </Button>
                                                                            </div>
                                                                        </td>
                                                                    )}
                                                                    {canCorrect && (
                                                                        <td className="text-end">
                                                                            <Button
                                                                                variant="outline-warning"
                                                                                size="sm"
                                                                                onClick={() => openCorrectModal(allocation, lotIngredient)}
                                                                                disabled={parseFloat(allocation.quantity_used || 0) <= 0 || parseFloat(allocation.quantity_disassembled || 0) > 0}
                                                                            >
                                                                                Correct
                                                                            </Button>
                                                                        </td>
                                                                    )}
                                                                </tr>
                                                            )
                                                        })}
                                                    </tbody>
                                                    </>
                                                )
                                            })()}
                                        </Table>
                                    ) : (
                                        <p className="text-muted mb-0">No inventory allocated</p>
                                    )}
                                </Card.Body>
                            </Card>
                        )})
                    ) : (
                        <p className="text-muted">No ingredients in this production LOT</p>
                    )}
                </Card.Body>
            </Card>

            {/* Action Buttons */}
            <div className="d-flex gap-2">
                {isInProgress && (
                    <>
                        <Button
                            variant="success"
                            onClick={() => navigate(`/production/${id}/finalize`, { state: { returnFilters } })}
                        >
                            Finalize Production
                        </Button>
                        <Button
                            variant="danger"
                            onClick={() => setShowCancelModal(true)}
                        >
                            Cancel Production
                        </Button>
                    </>
                )}
                <Button
                    variant="outline-primary"
                    onClick={() => navigate(`/productions${returnFilters}`)}
                >
                    <i className="fas fa-arrow-left me-2"></i>
                    Back to List
                </Button>
            </div>

            {/* Add Inventory Modal */}
            {selectedIngredientForAdd && (
                <PickInventoryModal
                    show={showAddInventoryModal}
                    onHide={() => {
                        setShowAddInventoryModal(false)
                        setSelectedIngredientForAdd(null)
                    }}
                    ingredient={{
                        _id: selectedIngredientForAdd.recipe_ingredient,
                        ingredient_name: selectedIngredientForAdd.ingredient_name,
                        ...selectedIngredientForAdd.recipe_ingredient_data
                    }}
                    requiredQuantity={0} // No specific requirement for additional picks
                    recipeId={productionLot.recipe}
                    onConfirm={handleAddInventoryConfirm}
                    existingAllocations={null}
                />
            )}

            {/* Return Inventory Modal */}
            <Modal show={showReturnModal} onHide={() => setShowReturnModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Return Inventory</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedAllocationForReturn && (
                        <>
                            <p>
                                <strong>Item:</strong> {selectedAllocationForReturn.stock_item_name}
                            </p>
                            <p>
                                <strong>Available to Return:</strong> {selectedAllocationForReturn._available}
                            </p>
                            <Form.Group className="mb-3">
                                <Form.Label>Quantity to Return</Form.Label>
                                <QuantityPicker
                                    casePack={selectedAllocationForReturn.stock_item_case_pack || 1}
                                    uom={selectedAllocationForReturn.stock_item_uom || ''}
                                    value={returnQuantity}
                                    onChange={(qty) => setReturnQuantity(qty)}
                                    max={selectedAllocationForReturn._available}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Return to Warehouse</Form.Label>
                                <Select
                                    value={warehouses
                                        .map(wh => ({ value: wh._id, label: wh.name }))
                                        .find(opt => opt.value === selectedWarehouse) || null}
                                    onChange={(opt) => setSelectedWarehouse(opt?.value || '')}
                                    options={warehouses.map(wh => ({ value: wh._id, label: wh.name }))}
                                    placeholder="Select Warehouse..."
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Return to Location</Form.Label>
                                <Select
                                    value={locations
                                        .map(loc => ({ value: loc._id, label: loc.name, item_count: loc.item_count }))
                                        .find(opt => opt.value === returnLocationId) || null}
                                    onChange={(opt) => setReturnLocationId(opt?.value || '')}
                                    options={locations.map(loc => ({ value: loc._id, label: loc.name, item_count: loc.item_count }))}
                                    isDisabled={!selectedWarehouse}
                                    placeholder="Select Location..."
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    formatOptionLabel={(option) => (
                                        <span>
                                            {option.label}
                                            {option.item_count != null && (
                                                <span className={`location-badge${option.item_count === 0 ? ' is-empty' : ''}`}>
                                                    {option.item_count === 0 ? 'Empty' : `${option.item_count} items`}
                                                </span>
                                            )}
                                        </span>
                                    )}
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowReturnModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="warning"
                        onClick={handleReturnInventory}
                        disabled={returningInventory || !returnLocationId || !returnQuantity}
                    >
                        {returningInventory ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Returning...
                            </>
                        ) : (
                            'Return Inventory'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Dispose Inventory Modal */}
            <Modal show={showDisposeModal} onHide={() => setShowDisposeModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Dispose Inventory</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedAllocationForDispose && (
                        <>
                            <p>
                                <strong>Item:</strong> {selectedAllocationForDispose.stock_item_name}
                            </p>
                            <p>
                                <strong>Available to Dispose:</strong> {selectedAllocationForDispose._available}
                            </p>
                            <Form.Group className="mb-3">
                                <Form.Label>Quantity to Dispose <span className="text-danger">*</span></Form.Label>
                                <QuantityPicker
                                    casePack={selectedAllocationForDispose.stock_item_case_pack || 1}
                                    uom={selectedAllocationForDispose.stock_item_uom || ''}
                                    value={disposeQuantity}
                                    onChange={(qty) => setDisposeQuantity(qty)}
                                    max={selectedAllocationForDispose._available}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Reason <span className="text-danger">*</span></Form.Label>
                                <Select
                                    value={reasonOptions
                                        .filter(opt => opt.value)
                                        .map(opt => ({ value: opt.value, label: opt.label }))
                                        .find(opt => opt.value === disposeReason) || null}
                                    onChange={(opt) => setDisposeReason(opt?.value || '')}
                                    options={reasonOptions
                                        .filter(opt => opt.value)
                                        .map(opt => ({ value: opt.value, label: opt.label }))}
                                    placeholder="Select Reason..."
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Comments</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={disposeComments}
                                    onChange={(e) => setDisposeComments(e.target.value)}
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDisposeModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleDisposeInventory}
                        disabled={disposingInventory || !disposeQuantity || !disposeReason}
                    >
                        {disposingInventory ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Disposing...
                            </>
                        ) : (
                            'Dispose Inventory'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Correct Ingredient Modal */}
            <Modal show={showCorrectModal} onHide={() => setShowCorrectModal(false)} size="lg" backdrop="static" keyboard={false}>
                <Modal.Header closeButton>
                    <Modal.Title>Correct Ingredient Usage</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Nav variant="tabs" className="mb-3" activeKey={correctModalTab} onSelect={(k) => setCorrectModalTab(k)}>
                        <Nav.Item>
                            <Nav.Link eventKey="reduce">Reduce Quantity</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="add">Add Item</Nav.Link>
                        </Nav.Item>
                    </Nav>

                    {correctModalTab === 'reduce' && selectedAllocationForCorrect && (() => {
                        const aCp = selectedAllocationForCorrect.stock_item_case_pack || 1
                        const aUom = selectedAllocationForCorrect.stock_item_uom || ''
                        const oldQty = parseFloat(selectedAllocationForCorrect.quantity_used || 0)
                        const newQty = parseFloat(correctNewQuantityUsed || 0)
                        const delta = oldQty - newQty
                        return (
                            <>
                                <p><strong>Item:</strong> {selectedAllocationForCorrect.stock_item_name}</p>
                                <p><strong>GTIN:</strong> <CopyableText value={selectedAllocationForCorrect.stock_item_gtin} toastLabel="GTIN" /></p>
                                {selectedAllocationForCorrect.original_lot && (
                                    <p><strong>LOT:</strong> {selectedAllocationForCorrect.original_lot}</p>
                                )}
                                <p>
                                    <strong>Current Quantity Used:</strong>{' '}
                                    <QuantityDisplay casePack={aCp} uom={aUom} value={oldQty} />
                                </p>
                                <hr />
                                <Form.Group className="mb-3">
                                    <Form.Label>New Quantity Used <span className="text-danger">*</span></Form.Label>
                                    <QuantityPicker
                                        casePack={aCp}
                                        uom={aUom}
                                        value={correctNewQuantityUsed}
                                        onChange={(qty) => setCorrectNewQuantityUsed(qty)}
                                        max={oldQty}
                                    />
                                </Form.Group>
                                {delta > 0 && (
                                    <Alert variant="info">
                                        <strong>Quantity to restore:</strong>{' '}
                                        <QuantityDisplay casePack={aCp} uom={aUom} value={delta} />
                                    </Alert>
                                )}
                                <h6>Restore Destination</h6>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Warehouse <span className="text-danger">*</span></Form.Label>
                                            <Select
                                                value={warehouses
                                                    .map(wh => ({ value: wh._id, label: wh.name }))
                                                    .find(opt => opt.value === selectedWarehouse) || null}
                                                onChange={(opt) => {
                                                    setSelectedWarehouse(opt?.value || '')
                                                    setReturnLocationId('')
                                                }}
                                                options={warehouses.map(wh => ({ value: wh._id, label: wh.name }))}
                                                placeholder="Select Warehouse..."
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Location <span className="text-danger">*</span></Form.Label>
                                            <Select
                                                value={locations
                                                    .map(loc => ({ value: loc._id, label: loc.name, item_count: loc.item_count }))
                                                    .find(opt => opt.value === returnLocationId) || null}
                                                onChange={(opt) => setReturnLocationId(opt?.value || '')}
                                                options={locations.map(loc => ({ value: loc._id, label: loc.name, item_count: loc.item_count }))}
                                                isDisabled={!selectedWarehouse}
                                                placeholder="Select Location..."
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                formatOptionLabel={(option) => (
                                                    <span>
                                                        {option.label}
                                                        {option.item_count != null && (
                                                            <span className={`location-badge${option.item_count === 0 ? ' is-empty' : ''}`}>
                                                                {option.item_count === 0 ? 'Empty' : `${option.item_count} items`}
                                                            </span>
                                                        )}
                                                    </span>
                                                )}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </>
                        )
                    })()}

                    {correctModalTab === 'add' && correctModalLotIngredient && (
                        <div className="text-center py-3">
                            <p>Add inventory items for <strong>{correctModalLotIngredient.ingredient_name}</strong> that were used but not originally recorded.</p>
                            <Button variant="success" onClick={handleSwitchToAddItem}>
                                <i className="fas fa-plus me-2"></i>
                                Pick Inventory Items
                            </Button>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-primary" onClick={() => setShowCorrectModal(false)}>
                        Cancel
                    </Button>
                    {correctModalTab === 'reduce' && (
                        <Button
                            variant="warning"
                            onClick={handleCorrectIngredient}
                            disabled={correctingIngredient || !returnLocationId ||
                                parseFloat(correctNewQuantityUsed || 0) >= parseFloat(selectedAllocationForCorrect?.quantity_used || 0) ||
                                parseFloat(correctNewQuantityUsed || 0) < 0}
                        >
                            {correctingIngredient ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Correcting...
                                </>
                            ) : (
                                'Apply Correction'
                            )}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>

            {/* Add Item Correction Modal (reuses PickInventoryModal) */}
            {correctModalLotIngredient && (
                <PickInventoryModal
                    show={showAddItemCorrectionModal}
                    onHide={() => {
                        setShowAddItemCorrectionModal(false)
                        setCorrectModalLotIngredient(null)
                    }}
                    ingredient={{
                        _id: correctModalLotIngredient.recipe_ingredient,
                        ingredient_name: correctModalLotIngredient.ingredient_name,
                        ...correctModalLotIngredient.recipe_ingredient_data
                    }}
                    requiredQuantity={0}
                    recipeId={productionLot.recipe}
                    onConfirm={handleAddItemCorrectionConfirm}
                    existingAllocations={null}
                />
            )}

            {/* Correct Output Quantity Modal */}
            <Modal show={showCorrectOutputModal} onHide={() => setShowCorrectOutputModal(false)} backdrop="static" keyboard={false}>
                <Modal.Header closeButton>
                    <Modal.Title>Correct Output Quantity</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {(() => {
                        const outputSi = productionLot.output_inventory_item_data
                        const oCp = outputSi?.stock_item?.case_pack || 1
                        const oUom = outputSi?.stock_item?.uom || ''
                        const oldQty = parseFloat(productionLot.actual_quantity_produced || 0)
                        const newQty = parseFloat(correctOutputNewQuantity || 0)
                        const delta = newQty - oldQty
                        const isTouched = outputSi && parseFloat(outputSi.quantity || 0) !== oldQty
                        return (
                            <>
                                <p>
                                    <strong>Current Output Quantity:</strong>{' '}
                                    <QuantityDisplay casePack={oCp} uom={oUom} value={oldQty} />
                                </p>
                                <hr />
                                <Form.Group className="mb-3">
                                    <Form.Label>New Output Quantity <span className="text-danger">*</span></Form.Label>
                                    <QuantityPicker
                                        casePack={oCp}
                                        uom={oUom}
                                        value={correctOutputNewQuantity}
                                        onChange={(qty) => setCorrectOutputNewQuantity(qty)}
                                    />
                                </Form.Group>
                                {delta !== 0 && !isNaN(delta) && (
                                    <Alert variant={delta > 0 ? 'info' : 'warning'}>
                                        <strong>{delta > 0 ? 'Increase' : 'Decrease'}:</strong>{' '}
                                        <QuantityDisplay casePack={oCp} uom={oUom} value={Math.abs(delta)} />
                                    </Alert>
                                )}
                                {delta < 0 && isTouched && (
                                    <Alert variant="danger">
                                        Cannot decrease output quantity — the output item has been modified (moved, issued, or partially used).
                                    </Alert>
                                )}
                            </>
                        )
                    })()}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-primary" onClick={() => setShowCorrectOutputModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="warning"
                        onClick={handleCorrectOutputQuantity}
                        disabled={
                            correctingOutput ||
                            !correctOutputNewQuantity ||
                            parseFloat(correctOutputNewQuantity || 0) <= 0 ||
                            parseFloat(correctOutputNewQuantity || 0) === parseFloat(productionLot.actual_quantity_produced || 0)
                        }
                    >
                        {correctingOutput ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Correcting...
                            </>
                        ) : (
                            'Apply Correction'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Cancel Confirmation Modal */}
            <ConfirmationModal
                show={showCancelModal}
                onHide={() => setShowCancelModal(false)}
                onConfirm={handleCancelProduction}
                title="Cancel Production"
                message={`Are you sure you want to cancel this production LOT? Inventory will remain at the production location (${productionLot.production_location_name}).`}
                confirmButtonText={cancelling ? 'Cancelling...' : 'Cancel Production'}
                confirmButtonVariant="danger"
            />

        </Container>
    )
}

export default ProductionDetailScreen
